import {
  MouseEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {FetchedData} from '../../../common/FetchedData.interface';
import {FetchStatus} from '../../../common/FetchStatus.enum';
import {t} from '../../../common/getTranslations';
import {getDataset} from '../../../common/shared/utils/getDataset';
import {Loading} from '../../../design-system/atoms/Loading/Loading';
import {Text} from '../../../design-system/atoms/Text/Text';
import {ErrorArea} from '../../../design-system/organisms/ErrorArea/ErrorArea';
import {
  MOBILE_MAIN_TAB_HEIGHT_PX,
  PC_MAIN_TAB_HEIGHT_PX,
} from '../../../design-system/organisms/MainTab/constants';
import {MoreExpandAskList} from '../../../design-system/organisms/MoreExpandAskList/MoreExpandAskList';
import {Pagination} from '../../../design-system/organisms/Pagination/Pagination';
import {PC_HEADER_HEIGHT} from '../../../design-system/organisms/PcHeader/constants';
import {PagewebError} from '../../../errors/PagewebError.class';
import {MOBILE_HEADER_HEIGHT_PX} from '../../../header/constants';
import {putTiaraObjectProps} from '../../../logging/putTiaraObjectProps';
import {AskAnswer, AskData, AskDetail, AskStatus} from '../type';

export interface AskMyProps {
  isMobile: boolean;
  isWebView: boolean;
  currentPage?: number;
  totalCount: number;
  onChangePage: MouseEventHandler;
  fetchedAskList: FetchedData<AskData[]>;
  openAskDetail: (askId: number) => Promise<AskDetail | undefined>;
}

export function AskMy({
  isMobile,
  isWebView,
  currentPage,
  totalCount,
  onChangePage,
  fetchedAskList,
  openAskDetail,
}: AskMyProps) {
  const [currentAskId, setCurrentAskId] = useState<number | null>(null);
  const [askAnswers, setAskAnswers] = useState<Pick<AskAnswer, 'content'>[]>(
    []
  );

  const activeAskItemDomRef = useRef<HTMLElement>(null);

  const onClickAsk = useCallback(
    async (e: React.SyntheticEvent<HTMLElement, Event>) => {
      const newAskId = Number(getDataset(e.currentTarget, 'id'));

      if (!newAskId) {
        return;
      }
      // TODO : data-id를 사용하지 않고 id를 가져오는 방법으로 변경
      if (newAskId === currentAskId) {
        setCurrentAskId(null);
        setAskAnswers([]);
        return;
      }

      try {
        const askDetail = await openAskDetail(newAskId);

        if (!askDetail) {
          return;
        }

        const {answers, subject, content} = askDetail as AskDetail;

        const filteredAnswers: Pick<AskAnswer, 'subject' | 'content'>[] = (
          answers ?? []
        ).map(_answer => ({
          subject: _answer.subject,
          /**
           * 운영자가 답변한 내용은 <br> 태그만 내려올 걸로 예상되지만,
           * 방어로직 차원으로 reaplaceAll 추가
           */
          content: _answer.content.replaceAll('\n', '<br />'),
        }));

        filteredAnswers.unshift({
          subject,
          content,
        });

        setCurrentAskId(newAskId);
        setAskAnswers(filteredAnswers);
      } catch (error) {
        throw new PagewebError(error);
      }
    },
    [currentAskId, openAskDetail]
  );

  const AskComponent = useMemo(() => {
    const isStatus = (status: AskStatus) => {
      if (status === 'A' || status === 'P') {
        return false;
      } else if (status === 'E') {
        return true;
      }
      return false;
    };

    return fetchedAskList.data?.map((item, index) => {
      const {questionId, subject, status, regDate, regTime} = item;

      let createDt = undefined;

      if (regDate && regTime) {
        const year = Number(regDate.substring(0, 4));
        const month = Number(regDate.substring(4, 6));
        const day = Number(regDate.substring(6, 8));

        const hour = Number(regTime.substring(0, 2));
        const minute = Number(regTime.substring(2, 4));
        const second = Number(regTime.substring(4, 6));

        createDt = new Date(year, month - 1, day, hour, minute, second);
      }

      const isActive = !!(questionId && questionId === currentAskId);

      return (
        <MoreExpandAskList
          key={index}
          id={questionId.toString()}
          isActive={isActive}
          isLabelBadge={true}
          isResolved={isStatus(status)}
          title={subject}
          createDt={createDt}
          onClick={onClickAsk}
          detail={askAnswers}
          ref={activeAskItemDomRef}
        />
      );
    });
  }, [currentAskId, askAnswers, fetchedAskList, onClickAsk]);

  useEffect(() => {
    if (activeAskItemDomRef.current && currentAskId) {
      const y =
        activeAskItemDomRef.current.getBoundingClientRect().top +
        window.scrollY;

      const headerHeight = isWebView
        ? MOBILE_MAIN_TAB_HEIGHT_PX
        : isMobile
          ? MOBILE_HEADER_HEIGHT_PX + MOBILE_MAIN_TAB_HEIGHT_PX
          : PC_HEADER_HEIGHT + PC_MAIN_TAB_HEIGHT_PX;
      window.scrollTo({top: y - headerHeight, behavior: 'smooth'});
    }
  }, [currentAskId, isMobile, isWebView]);

  if (fetchedAskList.fetchStatus === FetchStatus.Error) {
    return (
      <ErrorArea isMobile={isMobile}>
        <ErrorArea.Title>{t('error_api_title')}</ErrorArea.Title>
        <ErrorArea.MainText>
          {t('more_customer_cs_history_tab_error_info')}
        </ErrorArea.MainText>
        <ErrorArea.LabelButton
          onClick={() => {
            window.location.reload();
          }}
        >
          {t('error_retry_button')}
        </ErrorArea.LabelButton>
      </ErrorArea>
    );
  }

  return (
    <main
      className="font-small1 leading-[21px] flex grow flex-col"
      {...putTiaraObjectProps({
        click: {layer1: '내 문의내역'},
      })}
    >
      {fetchedAskList.fetchStatus === FetchStatus.Loading && (
        <div className="flex-center-full">
          <Loading />
        </div>
      )}
      {fetchedAskList.data && fetchedAskList.data.length > 0 && (
        <>
          <div className="flex p-[30px_15px_0px_15px] items-start gap-[10px] self-stretch">
            <Text className="font-small2 text-theme-solid-40">
              {t('more_customer_cs_history_tab_info')}
            </Text>
          </div>

          <dl className="m-0" data-test="ask-my-list">
            {AskComponent}
          </dl>
          {/* 데스크톱 전용 UI */}
          {!isMobile && (
            <Pagination
              currentPage={currentPage}
              onChangePage={onChangePage}
              totalCount={totalCount}
            />
          )}
        </>
      )}

      {fetchedAskList.fetchStatus === FetchStatus.Success &&
        fetchedAskList.data?.length === 0 && (
          <div className="flex-center-full">
            <div className="flex justify-center h-62pxr">
              <Text className="text-center font-medium1 text-theme-solid-100">
                {t('more_customer_cs_history_tab_no_history')}
              </Text>
            </div>
          </div>
        )}
    </main>
  );
}
